import { dashboardErrorSchema } from "@raid-lens/contracts";

import {
  authenticatedGuildQueryCache,
  guildIdFromMutationPath,
  guildQueryKeyFromApiPath,
} from "./guild-query-cache";

const MAX_JSON_RESPONSE_BYTES = 256 * 1024;

interface ResponseSchema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type SafeRequestFailureKind = "forbidden" | "degraded" | "error";

export interface SafeRequestFailure {
  kind: SafeRequestFailureKind;
  reason: string;
  requestId?: string;
  retryAfterSeconds?: number;
}

export function toSafeRequestFailure(
  error: unknown,
  fallbackReason: string,
): SafeRequestFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "error", reason: fallbackReason };
  }
  return {
    kind:
      error.status === 403
        ? "forbidden"
        : error.status === 408 || error.status === 429 || error.status >= 500
          ? "degraded"
          : "error",
    reason: error.message,
    ...(error.requestId === undefined ? {} : { requestId: error.requestId }),
    ...(error.retryAfterSeconds === undefined
      ? {}
      : { retryAfterSeconds: error.retryAfterSeconds }),
  };
}

export class ApiConflictError<T> extends ApiError {
  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string,
    public readonly current: T,
  ) {
    super(status, code, message, requestId);
    this.name = "ApiConflictError";
  }
}

export async function apiGet<T>(
  path: string,
  schema: ResponseSchema<T>,
  signal?: AbortSignal,
): Promise<T> {
  const queryKey = guildQueryKeyFromApiPath(path);
  if (!queryKey) {
    return apiRequest(path, schema, {
      method: "GET",
      ...(signal === undefined ? {} : { signal }),
    });
  }
  return authenticatedGuildQueryCache.query(
    queryKey,
    (sharedSignal) =>
      apiRequest(path, schema, { method: "GET", signal: sharedSignal }),
    signal,
  );
}

export async function apiMutation<T>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  schema: ResponseSchema<T>,
  body?: unknown,
  signal?: AbortSignal,
  conflictSchema?: ResponseSchema<{
    code: string;
    message: string;
    requestId: string;
    current: T;
  }>,
): Promise<T> {
  invalidateMutationQueries(path);
  return apiRequest(
    path,
    schema,
    {
      method,
      ...(signal === undefined ? {} : { signal }),
      ...(body === undefined
        ? {}
        : {
            body: JSON.stringify(body),
            headers: { "Content-Type": "application/json" },
          }),
    },
    conflictSchema,
  );
}

export interface NoContentMutationOptions<T> {
  body?: unknown;
  signal?: AbortSignal;
  conflictSchema?: ResponseSchema<{
    code: string;
    message: string;
    requestId: string;
    current: T;
  }>;
}

export async function apiMutationNoContent<T = never>(
  path: string,
  method: "POST" | "DELETE",
  options: NoContentMutationOptions<T> = {},
): Promise<void> {
  const { body, signal, conflictSchema } = options;
  invalidateMutationQueries(path);
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  });
  if (!response.ok) {
    const payload = await readPayloadAndClearAuthorization(response);
    if (response.status === 409 && conflictSchema !== undefined) {
      const conflict = conflictSchema.safeParse(payload);
      if (conflict.success) {
        throw new ApiConflictError(
          response.status,
          conflict.data.code,
          conflict.data.message,
          conflict.data.requestId,
          conflict.data.current,
        );
      }
    }
    const parsedError = dashboardErrorSchema.safeParse(payload);
    throw new ApiError(
      response.status,
      parsedError.success ? parsedError.data.code : "unexpected_response",
      parsedError.success
        ? parsedError.data.message
        : "RaidLens could not complete this request.",
      parsedError.success ? parsedError.data.requestId : undefined,
      parsedError.success ? parsedError.data.retryAfterSeconds : undefined,
    );
  }
  if (response.status !== 204) {
    throw new ApiError(
      response.status,
      "invalid_response",
      "RaidLens returned an unexpected response.",
    );
  }
  if (path === "/auth/logout") {
    authenticatedGuildQueryCache.clearSession();
  }
}

async function apiRequest<T>(
  path: string,
  schema: ResponseSchema<T>,
  init: RequestInit,
  conflictSchema?: ResponseSchema<{
    code: string;
    message: string;
    requestId: string;
    current: T;
  }>,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  const payload = await readPayloadAndClearAuthorization(response);

  if (!response.ok) {
    if (response.status === 409 && conflictSchema !== undefined) {
      const conflict = conflictSchema.safeParse(payload);
      if (conflict.success) {
        throw new ApiConflictError(
          response.status,
          conflict.data.code,
          conflict.data.message,
          conflict.data.requestId,
          conflict.data.current,
        );
      }
    }
    const parsedError = dashboardErrorSchema.safeParse(payload);
    throw new ApiError(
      response.status,
      parsedError.success ? parsedError.data.code : "unexpected_response",
      parsedError.success
        ? parsedError.data.message
        : "RaidLens could not complete this request.",
      parsedError.success ? parsedError.data.requestId : undefined,
      parsedError.success ? parsedError.data.retryAfterSeconds : undefined,
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      "invalid_response",
      "RaidLens returned an unexpected response.",
    );
  }
  return parsed.data;
}

async function readPayloadAndClearAuthorization(
  response: Response,
): Promise<unknown> {
  try {
    return await readBoundedJson(response);
  } finally {
    if (response.status === 401 || response.status === 403) {
      authenticatedGuildQueryCache.handleAuthorizationFailure(response.status);
    }
  }
}

function invalidateMutationQueries(path: string): void {
  const guildId = guildIdFromMutationPath(path);
  if (guildId) authenticatedGuildQueryCache.invalidateGuild(guildId);
}

async function readBoundedJson(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  const contentLength = Number(response.headers.get("content-length"));
  if (
    !contentType.toLowerCase().includes("application/json") ||
    (Number.isFinite(contentLength) && contentLength > MAX_JSON_RESPONSE_BYTES)
  ) {
    return undefined;
  }

  if (response.body === null) return undefined;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let byteLength = 0;
  let text = "";
  let chunk = await reader.read();
  while (!chunk.done) {
    byteLength += chunk.value.byteLength;
    if (byteLength > MAX_JSON_RESPONSE_BYTES) {
      await reader.cancel();
      return undefined;
    }
    text += decoder.decode(chunk.value, { stream: true });
    chunk = await reader.read();
  }
  text += decoder.decode();
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
